import React from 'react';
import { AlertTriangle, FileCheck2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { RequirementsTabProps } from './types';

const RequirementsTab: React.FC<RequirementsTabProps> = ({
    node,
    handleUpdate,
    testamentTypeMap,
    loading,
    repositoryAvailable,
    onAddRequirementRequest
}) => {
    const requirements = node.requirements || [];

    const handleRemoveRequirement = (indexToRemove: number) => {
        handleUpdate('requirements', requirements.filter((_, idx) => idx !== indexToRemove));
    };

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-teal-50 border border-teal-100 rounded-xl">
                    <div className="text-2xl font-bold text-teal-700">{requirements.length}</div>
                    <div className="text-xs text-teal-600 font-medium">Required Testaments</div>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                    <div className="text-2xl font-bold text-slate-700">{testamentTypeMap.size}</div>
                    <div className="text-xs text-slate-500 font-medium">Resolved Names</div>
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Requirements</h3>
                    <button
                        onClick={onAddRequirementRequest}
                        disabled={!repositoryAvailable}
                        className="text-xs bg-teal-600 hover:bg-teal-700 disabled:bg-slate-200 disabled:text-slate-400 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 transition-colors"
                    >
                        <Plus size={12} /> Add Requirement
                    </button>
                </div>

                {!repositoryAvailable && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2 text-xs text-amber-800">
                        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                        <span>Testament type repository is not configured.</span>
                    </div>
                )}

                <div className="space-y-2">
                    {requirements.map((requirement, idx) => {
                        const testamentType = testamentTypeMap.get(requirement.testamentTypeId);
                        return (
                            <div key={`${requirement.testamentTypeId}-${idx}`} className="p-3 rounded-lg border border-slate-200 bg-white shadow-sm group transition-all">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2 min-w-0">
                                        <FileCheck2 size={16} className="text-teal-600 mt-0.5 flex-shrink-0" />
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-sm text-slate-900 truncate" title={requirement.testamentTypeId}>
                                                    {loading ? 'Loading...' : (testamentType?.name || requirement.testamentTypeId)}
                                                </span>
                                                {testamentType?.isDeprecated && (
                                                    <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 rounded border border-amber-200">Deprecated</span>
                                                )}
                                            </div>
                                            {testamentType?.description && (
                                                <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                                                    {testamentType.description}
                                                </div>
                                            )}
                                            <div className="flex flex-wrap gap-1.5 mt-2 text-[10px] text-slate-500">
                                                {testamentType?.category && (
                                                    <span className="bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">{testamentType.category}</span>
                                                )}
                                                {testamentType?.requiresDocument && (
                                                    <span className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-100">Document</span>
                                                )}
                                                {testamentType?.isRenewable && (
                                                    <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-100 flex items-center gap-1">
                                                        <RefreshCw size={9} /> Renewable
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveRequirement(idx)}
                                        className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                        title="Remove requirement"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    {requirements.length === 0 && (
                        <div className="text-center py-8 bg-slate-50 border border-dashed border-slate-200 rounded-xl text-slate-400 text-sm">
                            No testament requirements configured.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RequirementsTab;
